import React, { useEffect, useState } from "react";
import {
  FaPlus,
  FaFilter,
  FaEye,
  FaTrash,
  FaStore,
  FaMoneyBillWave,
  FaPills,
  FaEdit,
  FaSearch
} from "react-icons/fa";
import {
  FiChevronLeft,
  FiChevronRight,
  FiBox,
  FiX,
  FiCheck,
  FiRefreshCw,
  FiLayers
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";

const ResepPage = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [drugs, setDrugs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState("");
  const [search, setSearch] = useState("");
  const [hargaStart, setHargaStart] = useState("");
  const [hargaEnd, setHargaEnd] = useState("");
  const [pageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalData, setTotalData] = useState(0);

  const navigate = useNavigate();
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const storedOutletId = localStorage.getItem("id_outlet");

  // Fetch Outlets
  const fetchOutlets = async () => {
    if (["owner", "admin", "superadmin"].includes(role)) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/v1/outlet`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        });
        const data = await response.json();
        setOutlets(data.data || []);
      } catch (error) {
        console.error("Fetch outlets error:", error);
      }
    }
  };

  // Fetch Data Paket
  const fetchDrugs = async () => {
    setLoading(true);
    let url = `${process.env.REACT_APP_API_BASE_URL}/v1/drug/pack/list?search=${search}&harga_jual_start=${hargaStart || 0}&harga_jual_end=${hargaEnd || 99999999}&page=${page}&per_page=${pageSize}`;
    
    if (!["owner", "admin", "superadmin"].includes(role)) {
      const outletId = storedOutletId;
      if (!outletId) {
        setLoading(false);
        return;
      }
      url += `&id_outlet=${outletId}`;
    } else if (selectedOutlet) {
      url += `&id_outlet=${selectedOutlet}`;
    }

    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
      });
      const data = await response.json();
      setDrugs(data.data || []);
      setTotalPages(data.meta?.last_page || 1);
      setTotalData(data.meta?.total || 0);
    } catch (error) {
      console.error("Fetch drug packs error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (["owner", "admin", "superadmin"].includes(role)) fetchOutlets();
    fetchDrugs();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [selectedOutlet, hargaStart, hargaEnd, search]);

  useEffect(() => {
    fetchDrugs();
  }, [page, selectedOutlet, hargaStart, hargaEnd, search]);

  const handleDeletePaket = async (id, name) => {
    Swal.fire({
      title: `Hapus Paket "${name}"?`,
      text: "Data paket resep akan dihapus permanen!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Ya, Hapus!",
      cancelButtonText: "Batal",
      customClass: { popup: "rounded-3xl" },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/v1/drug/pack/delete/${id}`,
            { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
          );
          const data = await response.json();
          if (response.ok && data.success !== false) {
            await Swal.fire({
              icon: "success",
              title: "Berhasil Dihapus!",
              text: `Paket "${name}" telah dihapus.`,
              timer: 1800,
              showConfirmButton: false,
              customClass: { popup: "rounded-3xl" },
            });
            fetchDrugs();
          } else {
            Swal.fire({
              icon: "error",
              title: "Gagal Menghapus",
              text: data.message || "Terjadi kesalahan server.",
              customClass: { popup: "rounded-3xl" },
            });
          }
        } catch (error) {
          console.error(error);
          Swal.fire({
            icon: "error",
            title: "Kesalahan Server",
            text: "Tidak dapat menghapus paket saat ini.",
            customClass: { popup: "rounded-3xl" },
          });
        }
      }
    });
  };

  const handleResetFilter = () => {
    setSelectedOutlet("");
    setHargaStart("");
    setHargaEnd("");
    setSearch("");
    setIsFilterOpen(false);
  };

  return (
    <div className="space-y-6 pb-12">
      {/* 1. Summary Cards */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-5"
      >
        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Total Paket Resep
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900">
              {loading ? "..." : totalData}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <FiBox className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Paket Halaman Ini
            </p>
            <h2 className="text-3xl font-extrabold text-teal-600">
              {loading ? "..." : drugs.length}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-teal-50 text-teal-600 border border-teal-100">
            <FaPills className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Filter Outlet Active
            </p>
            <h2 className="text-xl font-extrabold text-emerald-700 truncate max-w-[160px]">
              {selectedOutlet ? (outlets.find((o) => String(o.id) === String(selectedOutlet))?.name || "Filtered") : "Semua Outlet"}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <FiLayers className="w-6 h-6" />
          </div>
        </div>
      </motion.div>

      {/* 2. Control Bar & Table */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white p-6 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6"
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="relative w-full md:w-80">
            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Cari nama resep..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-semibold"
            />
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto justify-end">
            <button
              onClick={() => setIsFilterOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-2xl transition-all shadow-sm"
            >
              <FaFilter className="w-3.5 h-3.5 text-emerald-600" />
              <span>Filter</span>
            </button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/obat-paket/paket/create")}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold rounded-2xl shadow-lg shadow-emerald-600/20 transition-all"
            >
              <FaPlus className="w-3.5 h-3.5" />
              <span>Tambah Paket Baru</span>
            </motion.button>
          </div>
        </div>

        {/* Table Content */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
            <table className="w-full text-left text-xs text-gray-600">
              <thead>
                <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                  <th className="p-3.5 w-12 text-center">No</th>
                  <th className="p-3.5">Nama Resep Paket</th>
                  <th className="p-3.5 text-right">Tuslah</th>
                  <th className="p-3.5 text-right">Embalase</th>
                  <th className="p-3.5 text-right">Harga Jual</th>
                  <th className="p-3.5">Outlet Cabang</th>
                  <th className="p-3.5 text-center">Total Item Obat</th>
                  <th className="p-3.5 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {drugs.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="p-12 text-center text-gray-400 font-semibold">
                      Belum ada paket resep obat ditemukan
                    </td>
                  </tr>
                ) : (
                  drugs.map((item, index) => {
                    const nameFormatted = item.name.replace(/\s+/g, "_");
                    const outletFormatted = (item.outlet || "").replace(/\s+/g, "_");
                    return (
                      <tr key={item.id} className="hover:bg-emerald-50/20 transition-colors">
                        <td className="p-3.5 text-center font-bold text-gray-400">
                          {(page - 1) * pageSize + index + 1}
                        </td>
                        <td className="p-3.5 font-extrabold text-gray-900 text-sm">
                          {item.name}
                        </td>
                        <td className="p-3.5 text-right font-mono font-semibold text-gray-700">
                          Rp {(item.tuslah || 0).toLocaleString("id-ID")}
                        </td>
                        <td className="p-3.5 text-right font-mono font-semibold text-gray-700">
                          Rp {(item.embalase || 0).toLocaleString("id-ID")}
                        </td>
                        <td className="p-3.5 text-right font-mono font-extrabold text-emerald-700 text-sm">
                          Rp {(item.harga_jual || 0).toLocaleString("id-ID")}
                        </td>
                        <td className="p-3.5 font-semibold text-gray-700">
                          {item.outlet || "-"}
                        </td>
                        <td className="p-3.5 text-center">
                          <span className="inline-flex px-3 py-1 bg-emerald-50 text-emerald-700 font-mono font-bold rounded-full border border-emerald-200">
                            {item.total_item || 0} Obat
                          </span>
                        </td>
                        <td className="p-3.5 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => navigate(`/obat-paket/paket/detail/${nameFormatted}/${outletFormatted}`)}
                              className="p-2 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 transition-all"
                              title="Lihat Detail Paket"
                            >
                              <FaEye className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => navigate(`/obat-paket/paket/update/${nameFormatted}/${outletFormatted}`)}
                              className="p-2 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-700 transition-all"
                              title="Edit Paket"
                            >
                              <FaEdit className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeletePaket(item.id, item.name)}
                              className="p-2 rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-100 transition-all"
                              title="Hapus Paket"
                            >
                              <FaTrash className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-gray-100 gap-3 text-xs">
          <p className="text-gray-400">
            Menampilkan <span className="font-bold text-gray-700">{drugs.length}</span> dari{" "}
            <span className="font-bold text-gray-700">{totalData}</span> paket resep
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page <= 1}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                page <= 1
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs font-semibold text-gray-600 px-2">
              Halaman {page} dari {totalPages}
            </span>
            <button
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={page >= totalPages}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                page >= totalPages
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* FILTER MODAL */}
      <AnimatePresence>
        {isFilterOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden border border-gray-100 flex flex-col"
            >
              <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-2xl bg-emerald-600 text-white shadow-md shadow-emerald-500/20">
                    <FaFilter className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Filter Paket Resep</h2>
                    <p className="text-xs text-gray-500">Filter berdasarkan outlet dan harga</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsFilterOpen(false)}
                  className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-white/80 transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4 text-xs">
                {["owner", "admin", "superadmin"].includes(role) && (
                  <div className="space-y-1.5">
                    <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider flex items-center gap-1.5">
                      <FaStore className="text-emerald-600" /> Outlet Cabang
                    </label>
                    <select
                      value={selectedOutlet}
                      onChange={(e) => setSelectedOutlet(e.target.value)}
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                    >
                      <option value="">Semua Outlet</option>
                      {outlets.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="space-y-1.5">
                  <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider flex items-center gap-1.5">
                    <FaMoneyBillWave className="text-emerald-600" /> Rentang Harga Jual (Rp)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="Mulai..."
                      value={hargaStart}
                      onChange={(e) => setHargaStart(e.target.value)}
                      className="p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                    />
                    <input
                      type="number"
                      placeholder="Maksimal..."
                      value={hargaEnd}
                      onChange={(e) => setHargaEnd(e.target.value)}
                      className="p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                    />
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-gray-100 flex items-center justify-between gap-3 bg-gray-50/50">
                <button
                  onClick={handleResetFilter}
                  className="px-4 py-2.5 rounded-2xl bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-semibold transition-colors flex items-center gap-1.5"
                >
                  <FiRefreshCw className="w-3.5 h-3.5" />
                  <span>Reset</span>
                </button>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsFilterOpen(false)}
                    className="px-4 py-2.5 rounded-2xl bg-white border border-gray-200 hover:bg-gray-100 text-gray-700 text-xs font-semibold transition-colors"
                  >
                    Batal
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setPage(1);
                      fetchDrugs();
                      setIsFilterOpen(false);
                    }}
                    className="px-5 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-1.5"
                  >
                    <FiCheck className="w-4 h-4" />
                    <span>Terapkan</span>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ResepPage;
